import {
  RetailerActions,
  RetailerMarker,
  RetailerEntity,
  RetailerType,
  RetailerPartnership,
  RetailerCategory,
} from './types';
import { ActionType } from 'src/store';
import { GlobalFilterItem } from 'src/modules/global/components/filter/GlobalFilter';

import { updateObject } from '../../../utils/object';
import { getCoordinatesDistance, centerMap } from '../utils/map';

const initialState = {
  filters: [],
  retailers: [],
  // sort: CompanyStatus,
  markers: [],
  map: null,
  currentLocation: null,
};

// If there are no stores in the current location, find the closest one and return its coordinates and distance
const findClosestStore = (stores, map) => {
  const center = { lat: map.getCenter().lat(), lng: map.getCenter().lng() };
  const displayStore = stores.reduce(
    (closestStore, store) => {
      const storeLocation = { lat: store.lat, lng: store.lng };
      //Get distance from the center of the map to the store
      const distance = getCoordinatesDistance(center, storeLocation);
      return distance < closestStore.distance ? { ...storeLocation, distance } : closestStore;
    },
    { distance: 9999 } // Initial distance furthest possible
  );

  const bounds = new google.maps.LatLngBounds(center);
  bounds.extend(displayStore);
  map.fitBounds(bounds);
  // map.setCenter(closestStore);
};

// Gets markers or stores based on the current configuration (filters, map visibility)
const getFilteredVisibleMarkers = (
  map: google.maps.Map,
  markers: RetailerMarker[],
  filters: GlobalFilterItem[],
  retailers?: RetailerEntity[]
) => {
  if (!map || !markers.length) {
    return retailers;
  }
  const bounds = map.getBounds();

  const visibleMarkers = markers.filter(
    (marker) => bounds && bounds.contains(marker.getPosition())
  );

  const visibleFilteredMarkers = visibleMarkers.filter((retailer) => {
    const isVisible =
      filters && filters.length
        ? filters.some(
            (filter) =>
              filter.id === retailer.category || retailer.category === RetailerCategory.LivingOffice
          )
        : true;
    // Set google maps marker visibility
    retailer.setVisible(isVisible);
    return isVisible;
  });

  if (visibleFilteredMarkers.length) {
    return sortMarkers(visibleFilteredMarkers);
  }

  //If there are no markers(ignoring filtering), go to the closest one
  if (!visibleMarkers.length && !visibleFilteredMarkers.length) {
    findClosestStore(markers, map);
  }
  return [];
};

//Sort markers based on selected sort method
const sortMarkers = (markers) => {
  const sortedRetailers = [];
  const brandPartners = [];
  const selectedBrandPartners = [];
  markers
    .sort((a, b) => (a.distance < b.distance ? -1 : 1))
    .forEach((marker) => {
      const { type, partnership } = marker;
      if (type === RetailerType.BrandSpace) {
        sortedRetailers.push(marker);
      } else {
        if (partnership === RetailerPartnership.selectedBrandPartner) {
          selectedBrandPartners.push(marker);
        } else {
          brandPartners.push(marker);
        }
      }
    });
  sortedRetailers.push(...selectedBrandPartners, ...brandPartners);

  return sortedRetailers;
};

// Calculates for each marker the distance to the current location and saves it in the marker
const setCurrentLocationDistance = (markers: RetailerMarker[], currentLocation) => {
  return markers.forEach((marker) => {
    marker.distance = getCoordinatesDistance(currentLocation, { lat: marker.lat, lng: marker.lng });
  });
};

//Set current location action
const setCurrentLocation = (state, action) => {
  const { map, markers, retailers } = state;
  const { location: currentLocation } = action.payload;
  const updatedMarkers = markers || retailers;
  setCurrentLocationDistance(updatedMarkers, currentLocation);
  if (map) {
    centerMap(map, currentLocation);
    // markers &&
    //   (updatedMarkers = getFilteredVisibleMarkers(map, updatedMarkers, storeFilters, Closest));
  }
  return updateObject(state, {
    visibleRetailers: updatedMarkers,
    currentLocation,
    // sort: Closest,
  });
};

// Main reducer function which checks the store action that has been triggered
const reducer = (state = initialState, action: ActionType) => {
  // Store actions
  const {
    SetFilter,
    SetMap,
    SetMarkers,
    SetCurrentLocation,
    ChangeMapBounds,
    FetchRetailerSuccess,
  } = RetailerActions;

  // Current state variables
  const { map, markers, filters, retailers } = state;
  switch (action.type) {
    case FetchRetailerSuccess: {
      const { payload: retailers } = action;
      const visibleRetailers = getFilteredVisibleMarkers(map, markers, filters, retailers);
      return {
        ...state,
        visibleRetailers,
        retailers,
      };
    }
    case SetFilter: {
      const { filters } = action.payload;
      const visibleRetailers = getFilteredVisibleMarkers(map, markers, filters);

      return updateObject(state, { visibleRetailers, filters });
    }
    case SetMap: {
      return updateObject(state, { map: action.payload.map });
    }
    case ChangeMapBounds: {
      return updateObject(state, {
        visibleRetailers: getFilteredVisibleMarkers(map, markers, filters, retailers),
      });
    }
    case SetMarkers: {
      const { markers } = action.payload;
      if (!markers[0]?.distance && state.currentLocation) {
        setCurrentLocationDistance(markers, state.currentLocation);
      }
      return updateObject(state, {
        markers: action.payload.markers,
      });
    }
    case SetCurrentLocation: {
      return setCurrentLocation(state, action);
    }
    default:
      return state;
  }
};

export default reducer;
